/*
 * Copyright © 2025. Legalbird eine Marke der Legalbird GmbH
 */

import { AbstractCase } from "../types/Entities/AbstractCase";
import FormSubmitProps from "../types/FormSubmitProps";
import { arrayUtils } from "./utils";
import { MultipleFileUploadProps } from "../components/Form/Common/FileUpload/MultipleFileUpload";
import { FileUploadSectionProps } from "../components/Form/Common/FileUploadSection/FileUploadSection";
import { isFlexServiceProduct } from "./advocardService";

type DynamicValue<A> = ({}: { product: AbstractCase }) => A;

export type MultipleFileUploadConfig = Pick<MultipleFileUploadProps, "fieldname" | "electronicFileFolderPath"> & {
  type: "MultipleFileUpload";
  isHidden?: DynamicValue<boolean>;
  isRequired?: DynamicValue<boolean>;
  headline?: DynamicValue<string>;
  subHeadline?: DynamicValue<string>;
  description: DynamicValue<string>;
};

export type FileUploadSectionConfig = Pick<FileUploadSectionProps, "fieldName" | "temporaryMediaObjectsFieldname"> & {
  type: "FileUploadSection";
  isHidden?: DynamicValue<boolean>;
  isRequired?: DynamicValue<boolean>;
  description?: DynamicValue<string>;
  headline?: DynamicValue<string>;
  helperText?: DynamicValue<string>;
};

export type DocumentsFormConfig = { mediaObjectTypeName?: string } & (FileUploadSectionConfig | MultipleFileUploadConfig);

export const getDocumentsFormConfig = ({ product, submitProps }: { product: AbstractCase; submitProps: FormSubmitProps }) => {
  let config: DocumentsFormConfig[] = [];

  let stepHeadline: string | undefined = undefined;
  let headline: string | undefined = undefined;

  function checkProperty(config: DocumentsFormConfig, property: "isRequired" | "isHidden") {
    return config[property]?.({ product }) ?? false;
  }

  const defendClaimDocuments: DocumentsFormConfig[] = [
    {
      type: "FileUploadSection",
      isHidden: ({ product }) => product.defenseProcessStatus !== "paymentOrder",
      mediaObjectTypeName: "paymentOrder",
      temporaryMediaObjectsFieldname: "temporaryFilesToCombine",
      fieldName: "paymentOrderDocument",
      description: () => "Mahnbescheid Teildatei",
      headline: () => "Mahnbescheid",
      helperText: () =>
        "Bitte laden Sie hier Ihren Mahnbescheid inklusive gelbem Umschlag vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden.",
      isRequired: () => true,
    },
    {
      type: "FileUploadSection",
      isHidden: ({ product }) => product.defenseProcessStatus !== "enforcementOrder",
      mediaObjectTypeName: "enforcementOrder",
      temporaryMediaObjectsFieldname: "temporaryFilesToCombine",
      fieldName: "enforcementOrderDocument",
      description: () => "Vollstreckungsbescheid Teildatei",
      headline: () => "Vollstreckungsbescheid",
      helperText: () =>
        "Bitte laden Sie hier Ihren Vollstreckungsbescheid vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden.",
      isRequired: () => true,
    },
    {
      type: "FileUploadSection",
      isHidden: ({ product }) => product.defenseProcessStatus !== "lawsuit",
      mediaObjectTypeName: "lawsuit",
      temporaryMediaObjectsFieldname: "temporaryFilesToCombine",
      fieldName: "lawsuitDocument",
      description: () => "Klage Teildatei",
      headline: () => "Klage Gegenseite",
      helperText: () =>
        "Bitte laden Sie hier die Ihnen zugegangene Klage vollständig hoch. Ohne diese Datei kann Ihr Fall nicht angelegt und von uns bearbeitet werden.",
      isRequired: () => true,
    },
    {
      type: "MultipleFileUpload",
      isHidden: ({ product }) => product.defenseProcessStatus !== "reminder",
      headline: () => "Unterlagen zur Forderung gegen Sie",
      subHeadline: () =>
        "Wenn Ihnen diese vorliegen, laden Sie hier bitte Unterlagen hoch, aus denen die Forderung gegen Sie entnommen werden kann. Zum Beispiel einen erhaltenen Brief oder den Screenshot einer E-Mail mit der Forderung. Wenn Ihnen nichts vorliegt, klicken Sie einfach auf “Weiter”.",
      fieldname: "reminderDocument",
      description: () => "Forderungsunterlagen",
      electronicFileFolderPath: "Außergerichtliches Verfahren",
    },
    {
      type: "MultipleFileUpload",
      isHidden: ({ product }) => product.defenseProcessStatus !== "debtCollection",
      headline: () => "Unterlagen zur Forderung gegen Sie",
      subHeadline: () =>
        "Wenn Ihnen diese vorliegen, laden Sie hier bitte Unterlagen hoch, aus denen die Forderung gegen Sie entnommen werden kann. Zum Beispiel einen erhaltenen Brief oder den Screenshot einer E-Mail mit der Forderung. Wenn Ihnen nichts vorliegt, klicken Sie einfach auf “Weiter”.",
      fieldname: "debtCollectionDocument",
      description: () => "Forderungsunterlagen",
      electronicFileFolderPath: "Außergerichtliches Verfahren",
    },
  ];

  switch (product.name) {
    case "eventContract":
    case "membershipContract":
    case "otherContract":
    case "providerContract":
    case "purchaseContract":
    case "serviceContract":
    case "subscriptionContract":
    case "travelContract":
    case "vehicleContract":
      stepHeadline = "Unterlagen";
      headline = "Bitte lassen Sie uns hier alle Unterlagen zu Ihrem Vertrag zukommen, damit wir Ihren Fall schnellstmöglich bearbeiten können.";

      const showMembershipTerminationDocuments =
        product.name === "membershipContract" && ["cancellationContractExtension", "cancellationRefused"].includes(product.reasonForDispute);

      config = [
        {
          type: "FileUploadSection",
          mediaObjectTypeName: "contractDocument",
          temporaryMediaObjectsFieldname: "contractDocumentFiles",
          fieldName: "contractFiles",
          description: () => "Vertrag",
          headline: () => "Vertrag" + (product.name === "purchaseContract" ? "/Bestellbestätigung" : ""),
          helperText: () =>
            "Bitte laden Sie hier Ihre Vertragsunterlagen hoch. Wenn kein Vertrag vorhanden ist, kann es auch die Bestellbestätigung oder die Rechnung sein. Achten Sie darauf, dass der Vertrag vollständig und gut lesbar ist.",
          isRequired: ({ product }) => ["enforceClaim", "defendClaim"].includes(product?.caseCategory ?? ""),
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Schriftverkehr mit dem Vertragspartner",
          subHeadline: () =>
            "Hatten Sie zu Ihrem aktuellen Anliegen bereits Schriftverkehr mit Ihrem Vertragspartner, z.B. E-Mails, Briefe, Whatsapp-Verläufe etc.? Dann wandeln Sie diese Unterlagen bitte in eine PDF-Datei um oder machen Sie einfach einen Screenshot davon und laden diesen dann entsprechend hier hoch.",
          fieldname: "contractPartnerCorrespondence",
          description: () => "Schriftverkehr Vertragspartner",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !["letterOfTermination"].includes(product.reasonForDispute) || !showMembershipTerminationDocuments,
          headline: () => "Kündigungsschreiben",
          subHeadline: () => "Bitte laden Sie hier das Kündigungsschreiben hoch, das Sie an den Vertragspartner geschickt haben.",
          fieldname: "letterOfTermination",
          description: () => "Kündigungsschreiben",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !["confirmationOfTermination"].includes(product.reasonForDispute) || !showMembershipTerminationDocuments,
          headline: () => "Kündigungsbestätigung",
          subHeadline: () => "Wenn Sie vom Anbieter eine Kündigungsbestätigung erhalten haben, können Sie diese hier bitte hochladen.",
          fieldname: "confirmationOfTermination",
          description: () => "Kündigungsbestätigung",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => product.name !== "vehicleContract",
          headline: () => "Zulassungsbescheinigung Teil 2 (Fahrzeugbrief)",
          subHeadline: () =>
            "Dieses Dokument weist Sie als rechtmäßigen Eigentümer des Fahrzeugs aus. Bitte stellen Sie sicher, dass alle Angaben gut lesbar sind und laden Sie eine vollständige Kopie hoch.",
          fieldname: "certificateOfRegistration",
          description: () => "Zulassungsbescheinigung",
        },
        ...defendClaimDocuments,
        {
          type: "MultipleFileUpload",
          headline: () => "Weitere Unterlagen",
          subHeadline: () => "Haben Sie noch weitere Unterlagen, die zum Verständnis Ihres Falls wichtig sind? Diese können Sie hier hochladen.",
          fieldname: "applicationDocument",
          description: () => "Weitere Unterlagen",
        },
      ];
      break;
    case "employmentContract":
      stepHeadline = "Unterlagen";
      headline =
        "Bitte lassen Sie uns hier alle Unterlagen zu Ihrem Arbeitsvertrag und Ihrem Anliegen zukommen, damit wir Ihren Fall schnellstmöglich bearbeiten können.";

      config = [
        {
          type: "FileUploadSection",
          mediaObjectTypeName: "contractDocument",
          temporaryMediaObjectsFieldname: "contractDocumentFiles",
          fieldName: "contractFiles",
          description: () => "Arbeitsvertrag",
          headline: () => "Arbeitsvertrag",
          helperText: () => "Bitte laden Sie hier Ihren Arbeitsvertrag in gut lesbar und inkl. aller Seiten und Anhänge hoch.",
          isRequired: ({ product }) => ["enforceClaim", "defendClaim"].includes(product?.caseCategory ?? ""),
        },
        {
          type: "MultipleFileUpload",
          fieldname: "correspondence",
          headline: () => "Schriftverkehr mit dem Arbeitgeber",
          subHeadline: () =>
            "Hatten Sie zu Ihrem aktuellen Anliegen bereits Schriftverkehr mit Ihrem Arbeitgeber, z.B. E-Mails, Briefe, WhatsApp-Verläufe etc.? Wenn ja, fotografieren Sie diese bitte ab oder machen Sie einfach einen Screenshot davon und laden Sie ihn hier hoch.",
          description: () => "Schriftverkehr mit dem Arbeitgeber",
          electronicFileFolderPath: "Mandantschaft",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["overtime", "overtimeClaim"]),
          headline: () => "Beleg für Überstunden",
          subHeadline: () =>
            "Bitte laden Sie hier alle Dokumente hoch, die Ihre geleisteten Überstunden belegen können. Dies können beispielsweise unterschriebene Stundenzettel, Auszüge aus elektronischen Zeiterfassungssystemen oder auch E-Mails und Chatverläufe sein, die Ihre Mehrarbeit dokumentieren. Jedes Detail hilft uns bei der Geltendmachung Ihrer Ansprüche.",
          fieldname: "overtime",
          description: () => "Nachweis über geleistete Überstunden",
          electronicFileFolderPath: "Mandantschaft",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["workCertificate"]),
          headline: () => "Arbeitszeugnis",
          subHeadline: () =>
            "Bitte laden Sie hier Ihr von Ihrem Arbeitgeber erhaltenes Arbeitszeugnis hoch. Dies ist unerlässlich für unsere Prüfung, insbesondere wenn Sie eine Korrektur wünschen.",
          fieldname: "workCertificate",
          description: () => "Arbeitszeugnis",
          electronicFileFolderPath: "Mandantschaft",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["writtenWarning"]),
          headline: () => "Abmahnung",
          subHeadline: () => "Bitte laden Sie hier Ihre Abmahnung hoch.",
          fieldname: "writtenWarning",
          description: () => "Abmahnung",
          electronicFileFolderPath: "Mandantschaft",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["termination"]),
          headline: () => "Aufhebungsvertrag",
          subHeadline: () => "Bitte laden Sie hier Ihren Aufhebungsvertrag hoch.",
          fieldname: "termination",
          description: () => "Aufhebungsvertrag",
          electronicFileFolderPath: "Mandantschaft",
        },
        {
          type: "MultipleFileUpload",
          isHidden: () => !arrayUtils.getArraysIntersect(product.reasonsForDispute, ["bullying"]),
          headline: () => "Nachweise über Mobbing/Diskriminierung",
          subHeadline: () =>
            "Bitte laden Sie hier alle Dokumente hoch, die die Vorfälle von Mobbing belegen. Dazu gehören insbesondere Ihr Mobbingtagebuch, aber auch relevanter Schriftverkehr (E-Mails, Nachrichten), interne Vermerke, ärztliche Atteste oder andere detaillierte Aufzeichnungen. Jede genaue Dokumentation stärkt Ihre rechtliche Position und hilft uns bei der Bearbeitung Ihres Falles.",
          fieldname: "incidentProof",
          description: () => "Nachweise über Mobbing/Diskriminierung",
          electronicFileFolderPath: "Mandantschaft",
        },
        ...defendClaimDocuments,
        {
          type: "MultipleFileUpload",
          headline: () => "Weitere Unterlagen",
          subHeadline: () => "Haben Sie noch weitere Unterlagen, die zum Verständnis Ihres Falls wichtig sind? Diese können Sie hier hochladen.",
          fieldname: "otherFiles",
          description: () => "Weitere Unterlagen",
          electronicFileFolderPath: "Mandantschaft",
        },
      ];
      break;
    case "rentalContract":
      const caseSpecificDocuments: DocumentsFormConfig[] = [
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !["housingDeficiencies"].includes(product.reasonForDispute),
          headline: () => "Bilder zu vorliegenden Mängeln",
          subHeadline: () => "Es liegen Mängel vor, die Sie mit Bildern darlegen können, dann lassen Sie uns diese bitte zukommen.",
          fieldname: "housingDeficienciesDocument",
          description: () => "Bilder Mängel",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !["deposit"].includes(product.reasonForDispute),
          headline: () => "Abnahmeprotokoll von Wohnungsübergabe",
          subHeadline: () => "Liegt Ihnen ein Abnahmeprotokoll von der Wohnungsübergabe vor? Dann laden Sie dieses gerne hier hoch.",
          fieldname: "acceptanceReportDocument",
          description: () => "Abnahmeprotokoll",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !["utilitiesStatement"].includes(product.reasonForDispute),
          headline: () => "Nebenkostenabrechnung",
          subHeadline: () => "Bitte laden Sie uns hier die Nebenkostenabrechnung in gut lesbarer Qualität und inklusive aller Seiten hoch.",
          fieldname: "utilitiesStatementDocument",
          description: () => "Nebenkostenabrechnung",
          isRequired: () => true,
        },
      ];

      if (isFlexServiceProduct(product)) {
        stepHeadline = "Unterlagen";
        headline =
          "Bitte lassen Sie uns hier alle Unterlagen zu Ihrem Mietvertrag und Ihrem Anliegen zukommen, damit wir Ihren Fall schnellstmöglich bearbeiten können.";

        config = [
          ...caseSpecificDocuments,
          ...defendClaimDocuments,
          {
            type: "MultipleFileUpload",
            headline: () => "Ihre Unterlagen",
            subHeadline: () => "Laden Sie hier bitte Ihre Unterlagen hoch, die zur weiteren Bearbeitung des Falls relevant sind.",
            fieldname: "applicationDocument",
            description: () => "Weitere Unterlagen",
          },
        ];

        break;
      }

      stepHeadline = "Unterlagen";
      headline =
        "Bitte lassen Sie uns hier alle Unterlagen zu Ihrem Mietvertrag und Ihrem Anliegen zukommen, damit wir Ihren Fall schnellstmöglich bearbeiten können.";

      config = [
        {
          type: "FileUploadSection",
          mediaObjectTypeName: "contractDocument",
          temporaryMediaObjectsFieldname: "contractDocumentFiles",
          fieldName: "contractFiles",
          description: () => "Mietvertrag",
          headline: () => "Mietvertrag",
          helperText: () => "Bitte laden Sie hier Ihren Mietvertrag in gut lesbar und inkl. aller Seiten und Anhänge hoch.",
          isRequired: ({ product }) => ["enforceClaim", "defendClaim"].includes(product?.caseCategory ?? ""),
        },
        {
          type: "MultipleFileUpload",
          headline: ({ product }) => "Schriftverkehr mit dem " + (product.contractPartyType === "landlord" ? "Mieter" : "Vermieter"),
          subHeadline: ({ product }) =>
            `Hatten Sie zu Ihrem aktuellen Anliegen bereits Schriftverkehr mit Ihrem ${
              product.contractPartyType === "landlord" ? "Mieter" : "Vermieter"
            }, z.B. E-Mails, Briefe, Whatsapp-Verläufe etc.? Dann fotografieren Sie diese bitte ab oder machen Sie einfach einen Screenshot davon und laden diesen dann entsprechend hier hoch.`,
          fieldname: "contractPartnerCorrespondence",
          description: ({ product }) => "Schriftverkehr mit dem " + (product.contractPartyType === "landlord" ? "Mieter" : "Vermieter"),
        },
        ...caseSpecificDocuments,
        ...defendClaimDocuments,
        {
          type: "MultipleFileUpload",
          headline: () => "Weitere Unterlagen",
          subHeadline: () => "Haben Sie noch weitere Unterlagen, die zum Verständnis Ihres Falls wichtig sind? Diese können Sie hier hochladen.",
          fieldname: "applicationDocument",
          description: () => "Weitere Unterlagen",
        },
      ];
      break;
    case "trafficAccident":
      stepHeadline = "Unterlagen";
      headline =
        "Damit wir Ihren Fall bestmöglich bearbeiten können, lassen Sie uns hier bitte alle Unterlagen zukommen, die Ihnen aktuell vorliegen. Sollten Ihnen noch nicht alle Unterlagen vorliegen, können Sie diese natürlich jederzeit nachreichen.";

      const damageEstimationDocumentConfig: Record<string, { headline: string; helperText: string }> = {
        costEstimate: {
          headline: "Kostenvoranschlag",
          helperText: "Bitte laden Sie hier den Kostenvoranschlag hoch, den Sie zur Abschätzung der voraussichtlichen Reparaturkosten erhalten haben.",
        },
        assessment: {
          headline: "Gutachten",
          helperText:
            "Bitte laden Sie hier das Sachverständigengutachten hoch, welches Sie zur Abschätzung der voraussichtlichen Reparaturkosten eingeholt haben.",
        },
        repairInvoice: {
          headline: "Reparaturrechnung",
          helperText: "Bitte laden Sie hier die Rechnung hoch, welche Sie von der Werkstatt für die Reparatur Ihres Fahrzeugs erhalten haben.",
        },
      };

      config = [
        {
          type: "FileUploadSection",
          isHidden: ({ product }) => !product.policeInvolved,
          fieldName: "policeReport",
          description: () => "Polizeibericht",
          headline: () => "Polizeibericht",
          helperText: () => "Sofern die Polizei den Unfall aufgenommen hat, laden Sie hier bitte das Dokument hoch, welches Sie erhalten haben.",
        },
        {
          type: "FileUploadSection",
          fieldName: "accidentSketch",
          description: () => "Unfallskizze",
          headline: () => "Unfallskizze",
          helperText: () =>
            "Eine grobe handschriftliche oder digitale Skizze des Unfallhergangs kann uns sehr dabei helfen, das Geschehen zu rekonstruieren und Ihren Fall besser einzuschätzen.",
        },
        {
          type: "FileUploadSection",
          isHidden: ({ product }) => !["vehicle", "vehicleAndPerson"].includes(product.typeOfDamage) || product.damageEstimationDocumentType === "noDocument",
          fieldName: "damageEstimationDocument",
          description: ({ product }) => damageEstimationDocumentConfig[product.damageEstimationDocumentType]?.headline,
          headline: ({ product }) => damageEstimationDocumentConfig[product.damageEstimationDocumentType]?.headline,
          helperText: ({ product }) => damageEstimationDocumentConfig[product.damageEstimationDocumentType]?.helperText,
        },
        {
          type: "FileUploadSection",
          isHidden: ({ product }) => !["person", "vehicleAndPerson"].includes(product.typeOfDamage),
          fieldName: "injuryDocumentation",
          description: () => "Dokumente zum Personenschaden",
          headline: () => "Dokumente zum Personenschaden",
          helperText: () =>
            "Sofern Ihnen Dokumente vorliegen, die die gesundheitlichen Schäden belegen, stellen Sie uns diese bitte zur Verfügung. Dies können zum Beispiel Arztberichte sein.",
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Fotos",
          subHeadline: () =>
            "Jegliche fotografische Darstellung zum Unfall oder zu den Schäden kann dabei helfen, Ihren Fall nachzuvollziehen und Ihre Forderungen zu belegen.",
          fieldname: "trafficAccidentPhotos",
          description: () => "Foto",
        },
      ];
      break;
    case "generalCivilLaw":
      stepHeadline = "Unterlagen";
      headline =
        "Bitte laden Sie alle Dokumente (Fotos, Screenshots, Schriftwechsel, Rechnungen...) hoch, die für Ihr Anliegen wichtig sind. Sollten Ihnen bislang keine Dokumente vorliegen, können Sie einfach in den nächsten Schritt wechseln.";

      config = [
        {
          type: "MultipleFileUpload",
          headline: () => "Fotos",
          subHeadline: () => "Laden Sie hier bitte Fotos hoch, die den Hergang dokumentieren oder beweisen können.",
          fieldname: "generalCivilLawPhotos",
          description: () => "Foto",
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Schriftwechsel",
          subHeadline: () =>
            "Wenn es Schriftwechsel mit der Gegenseite gab, dann laden Sie diesen bitte hier hoch. Dies können Screenshots von WhatsApp-Verläufen, E-Mails oder Briefe sein.",
          fieldname: "generalCivilLawCorrespondences",
          description: () => "Schriftwechsel mit Gegenseite",
        },
        {
          type: "MultipleFileUpload",
          isHidden: ({ product }) => !product.policeInvolved,
          headline: () => "Polizeibericht",
          subHeadline: () => "Sollte bereits ein Polizeibericht oder eine Anzeigenbestätigung vorliegen, lassen Sie uns diesen bitte zukommen.",
          fieldname: "policeReports",
          description: () => "Polizeibericht",
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Rechnungen",
          subHeadline: () => "Laden Sie hier bitte Rechnungen hoch, die im Zusammenhang mit dem Vorfall angefallen sind.",
          fieldname: "generalCivilLawBills",
          description: () => "Rechnung",
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Weitere Unterlagen",
          subHeadline: () => "Jegliche andere Dokumente, die zum Verständnis des Falls oder als Beweis sinnvoll sind.",
          fieldname: "generalCivilLawOtherDocuments",
          description: () => "Weitere Unterlagen",
        },
      ];
      break;
    case "civilServiceLaw":
      stepHeadline = "Unterlagen";

      headline =
        "Bitte laden Sie alle Dokumente (Bescheide, Schriftwechsel, Rechnungen...) hoch, die für Ihr Anliegen wichtig sind. Sollten Ihnen bislang keine Dokumente vorliegen, können Sie einfach in den nächsten Schritt wechseln.";

      config = [
        {
          type: "FileUploadSection",
          fieldName: "civilServiceLawOfficialLetter",
          headline: () => "Bescheid",
          helperText: () =>
            "Laden Sie hier bitte jede Seite des Bescheids hoch. Falls vorhanden, auch einen Nachweis über das Zustelldatum bei (z. B. den gelben Briefumschlag).",
          description: () => 'Bescheid ${product?.client?.person?.familyName || ""}',
          isRequired: ({ product }) => product?.hasReceivedAdministrativeDecision === true && product.caseCategory !== "advisory",
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Schriftwechsel",
          subHeadline: () => "Wenn es Schriftwechsel mit der Gegenseite gab, dann laden Sie diesen bitte hier hoch. Dies können E-Mails oder Briefe sein.",
          fieldname: "civilServiceLawCorrespondences",
          description: () => `Schriftwechsel Gegenseite ${product?.client?.person?.familyName || ""}`,
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Rechnungen",
          subHeadline: () => "Laden Sie hier bitte Rechnungen hoch, die im Zusammenhang mit dem Vorfall angefallen sind.",
          fieldname: "civilServiceLawInvoices",
          description: () => `Rechnung ${product?.client?.person?.familyName || ""}`,
        },
        {
          type: "MultipleFileUpload",
          headline: () => "Weitere Unterlagen",
          subHeadline: () => "Jegliche andere Dokumente, die zum Verständnis des Falls oder als Beweis sinnvoll sind",
          fieldname: "civilServiceLawOtherDocuments",
          description: () => "Weitere Unterlagen",
        },
      ];

      break;
  }

  const requiredDocuments = config.filter((config) => checkProperty(config, "isRequired") && !checkProperty(config, "isHidden"));
  const optionalDocuments = config.filter((config) => !checkProperty(config, "isRequired") && !checkProperty(config, "isHidden"));

  return {
    stepHeadline,
    headline,
    requiredDocuments,
    optionalDocuments,
    submitProps,
    product,
  };
};
